import { CREATE_RUN_AND_ASSIGN_MUTATION } from '@/graphql/runs.gql';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Dialog, Transition } from '@headlessui/react';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  CLPrimaryButton,
  CLPrimaryDate,
  CLPrimaryInput,
  CLPrimarySelect,
  CLSecondaryButton
} from '../../../ui-controls';

import { v4 as uuidv4 } from 'uuid';
import { LoadingSpinWhiteCustom } from '../../../utils/loading.component';

import ProjectMembersPopupComponent from '@/components/projects/project-memebers-popup.component';
import PlanAndBillingPopupComponent from '@/components/utils/plan-and-billing-popup.component';
import CLTooltip from '@/components/utils/tooltip';
import PERMISSIONS_CONSTANTS from '@/constants/permission-constants';
import { GET_PROJECT_USERS_QUERY } from '@/graphql/projects.gql';
import {
  CREATE_SCHEDULE_ASSIGNMENTS_MUTATION,
  CREATE_SCHEDULE_CONFIGURATION_MUTATION,
  UPDATE_NEXT_RUN_MUTATION
} from '@/graphql/schedule-run.gql';
import { useOrganizationStore } from '@/stores/organization.store';
import { useProjectsStore } from '@/stores/projects.store';
import { ProjectUser } from '@/types/projects.type';
import { useUserId } from '@nhost/nextjs';
import moment from 'moment-timezone';
import posthog from 'posthog-js';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import { shallow } from 'zustand/shallow';
import styles from '../../../../styles/ui-controls/Input.module.scss';
import CLButton from '../../../ui-controls/default-ui-controls/button';
import { fireNativeNotification } from '@/services/utils/utils.service';
import {
  GET_ASSIGNED_PREF,
  GET_GLOBAL_PREF,
  INSERT_NOTIFICATION
} from '@/graphql/notfications.gql';

interface PopupProps {
  projectId: string;
  checklistId: string;
  checklistTitle: string;
  checklistVersionId: string;
  isOpen: boolean;
  isFromDesignPage?: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export default function AssignChecklistPopupComponent({
  isOpen,
  setIsOpen,
  ...props
}: PopupProps) {
  // Variables
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const userId = useUserId();

  // States
  const [projectUsers, setProjectUsers] = useState<
    { id: string; displayName: string }[]
  >([]);
  const [runName, setRunName] = useState<string>('');
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [assignTo, setAssignTo] = useState<
    { id: string; displayName: string }[]
  >([]);

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<React.ReactNode>('');
  const [isPlanAndBillingPopupOpen, setIsPlanAndBillingPopupOpen] =
    useState<boolean>(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const [recurrenceOptions, setRecurrenceOptions] = useState<
    { id: string; displayName: string }[]
  >([]);
  const [recurrenceValue, setRecurrenceValue] = useState<{
    id: string;
    displayName: string;
  }>();
  const [repeatValue, setRepeatValue] = useState<{
    id: string;
    displayName: string;
  }>();
  const [dueDayValue, setDueDayValue] = useState<{
    id: string;
    displayName: string;
  }>();
  const [endDate, setEndDate] = useState<string | null>(null);
  const [repeatOnDays, setRepeatOnDays] = useState<number[]>([]);
  const weekDayOptions = [
    { id: 0, displayName: 'Sunday', label: 'Sun' },
    { id: 1, displayName: 'Monday', label: 'Mon' },
    { id: 2, displayName: 'Tuesday', label: 'Tue' },
    { id: 3, displayName: 'Wednesday', label: 'Wed' },
    { id: 4, displayName: 'Thursday', label: 'Thu' },
    { id: 5, displayName: 'Friday', label: 'Fri' },
    { id: 6, displayName: 'Saturday', label: 'Sat' }
  ];
  const tzList = moment.tz.names().map(tz => {
    const offset = moment.tz(tz).utcOffset() / 60;
    const gmtOffset =
      offset === 0
        ? 'GMT'
        : `GMT${offset >= 0 ? '+' : ''}${Math.floor(offset)}${
            (offset % 1) * 60 !== 0 ? `:${Math.abs((offset % 1) * 60)}` : ''
          }`;
    return {
      id: tz,
      fullLabel: `(UTC${offset >= 0 ? '+' : ''}${Math.floor(offset)
        .toString()
        .padStart(2, '0')}:${Math.abs((offset % 1) * 60)
        .toString()
        .padStart(2, '0')}) ${tz.replace('_', ' ')} [${gmtOffset}]`,
      label: tz.replace('_', ' '),
      value: tz
    };
  });

  const localTimeZone = moment.tz.guess();
  const defaultTimeZone = tzList.find(tz => tz.value === localTimeZone);

  const [timeZoneValue, setTimeZoneValue] = useState<(typeof tzList)[0] | null>(
    defaultTimeZone || null
  );

  // Initialize date to today, and time to “00:00” by default
  const today = new Date();
  const pad2 = (n: number) => n.toString().padStart(2, '0');

  const initialDate = `${today.getFullYear()}-${pad2(
    today.getMonth() + 1
  )}-${pad2(today.getDate() + 1)}`;
  const initialTime = '12:00 AM';

  const [startDate, setStartDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(initialTime);
  const [monthlyOption, setMonthlyOption] = useState<'date' | 'weekday'>(
    'date'
  );
  const [monthDates, setMonthDates] = useState<number[]>([]); // repeat_on_month_dates
  const [monthWeekNumber, setMonthWeekNumber] = useState<number | null>(1); // repeat_on_week_number
  const [monthWeekDay, setMonthWeekDay] = useState<number | null>(0); // repeat_on_week_day

  // Store
  const [selectedOrganization, organizationUsage] = useOrganizationStore(
    state => [state.selectedOrganization, state.organizationUsage],
    shallow
  );
  const [projectPermissions, newProjectIds] = useProjectsStore(
    s => [s.projectPermissions, s.newProjectIds],
    shallow
  );

  // GraphQL
  const { data, loading, error, refetch } = useQuery(GET_PROJECT_USERS_QUERY, {
    variables: { projectId: props.projectId }
  });
  const [createRunAndAssignMutation] = useMutation(
    CREATE_RUN_AND_ASSIGN_MUTATION
  );
  const [createScheduleConfiguration, { loading: isCreatingSchedule }] =
    useMutation(CREATE_SCHEDULE_CONFIGURATION_MUTATION);
  const [createScheduleAssignments] = useMutation(
    CREATE_SCHEDULE_ASSIGNMENTS_MUTATION
  );
  const [updateNextRun] = useMutation(UPDATE_NEXT_RUN_MUTATION);

  useEffect(() => {
    const fetchData = async () => {
      if (!isInviteOpen) {
        try {
          await refetch(); // This refetch the query
        } catch (err) {
          console.error('Error fetching project users:', err);
        }
      }
    };

    fetchData();
    // Reset the values
    if (isOpen) {
      setErrorMsg('');
      setSuccessMsg('');
      setRunName(props.checklistTitle || '');
      setAssignTo([]);
      setStartTime(initialTime);
      setRecurrenceOptions([
        { id: 'Do Not Repeat', displayName: 'Do Not Repeat' },
        { id: 'DAILY', displayName: 'Daily' },
        { id: 'WEEKLY', displayName: 'Weekly' },
        { id: 'MONTHLY', displayName: 'Monthly' },
        { id: 'YEARLY', displayName: 'Yearly' }
      ]);
      setRecurrenceValue({
        id: 'Do Not Repeat',
        displayName: 'Do Not Repeat'
      });

      if (!dueDate) {
        setDueDate(tomorrowDate.toLocaleDateString('en-CA'));
      }
    }

    // Project users
    if (data && data?.project_users && data?.project_users?.length > 0) {
      const projectUsers = data?.project_users?.map(
        (projectInvite: ProjectUser) => {
          return {
            id: projectInvite?.user?.id,
            displayName:
              projectInvite?.user?.displayName || projectInvite?.user?.email
          };
        }
      );
      setProjectUsers(projectUsers);
    }
  }, [isOpen, data, isInviteOpen]);

  useEffect(() => {
    checkRunLimitation();
  }, [organizationUsage]);

  // Ask for permission once when component mounts
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        // console.log('Notification permission:', permission);
      });
    }
  }, []);

  const client = useApolloClient();

  // 1) Global browser setting (event_type IS NULL)
  const { data: globalData } = useQuery(GET_GLOBAL_PREF, {
    variables: { user_id: userId, org_id: selectedOrganization?.id },
    skip:
      !userId ||
      !selectedOrganization?.id ||
      !!selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_plan_type?.is_freeplan,
    fetchPolicy: 'network-only'
  });

  const [insertNotification] = useMutation(INSERT_NOTIFICATION);

  // 2) Assigned-to-you browser setting (filtered server‑side)
  // const { data: assignedData } = useQuery(GET_ASSIGNED_PREF, {
  //   variables: {
  //     user_id: userId,
  //     org_id: selectedOrganization?.id,
  //     event_type: 'assigned_to_you'
  //   },
  //   skip:
  //     !userId ||
  //     !selectedOrganization?.id ||
  //     !!selectedOrganization?.pricing_subscription?.pricing_plan
  //       ?.pricing_plan_type?.is_freeplan,
  //   fetchPolicy: 'network-only'
  // });

  // const { data: dueData } = useQuery(GET_ASSIGNED_PREF, {
  //   variables: {
  //     user_id: userId,
  //     org_id: selectedOrganization?.id,
  //     event_type: 'due_date_reached'
  //   },
  //   fetchPolicy: 'network-only'
  // });

  // const dueBrowserEnabled =
  //   dueData?.user_notification_preferences[0]?.browser_enabled ?? false;

  const globalBrowserEnabled =
    globalData?.user_notification_preferences[0]?.browser_enabled ?? false;

  // const assignedBrowserEnabled =
  //   assignedData?.user_notification_preferences[0]?.browser_enabled ?? false;

  async function getNotificationPreference(userId: string) {
    try {
      const { data } = await client.query({
        query: GET_ASSIGNED_PREF,
        variables: {
          user_id: userId,
          org_id: selectedOrganization?.id,
          event_type: 'assigned_to_you'
        },
        fetchPolicy: 'network-only'
      });
      // console.log('data:', data);

      return data?.user_notification_preferences[0]?.browser_enabled ?? false;
    } catch (err) {
      console.error(`Failed to get preference for user ${userId}`, err);
      return false; // default to false if error
    }
  }

  useEffect(() => {
    if (
      recurrenceValue?.id === 'DAILY' ||
      recurrenceValue?.id === 'WEEKLY' ||
      recurrenceValue?.id === 'MONTHLY' ||
      recurrenceValue?.id === 'YEARLY'
    ) {
      setRepeatValue({ id: '1', displayName: '1' });
      setRunName(
        recurrenceValue?.displayName + ' - ' + props.checklistTitle ||
          'Untitled'
      );
      setErrorMsg('');
      setSuccessMsg('');
    } else {
      setRunName(props.checklistTitle || '');
      setErrorMsg('');
      setSuccessMsg('');
    }
  }, [recurrenceValue]);

  function validate(): string | void {
    if (!runName) {
      return 'Please enter name';
    }

    if (!assignTo || assignTo?.length == 0) {
      return 'Please select "Assign to"';
    }

    if (recurrenceValue?.id !== 'Do Not Repeat' && !endDate) {
      return 'Please select an End Date for the schedule';
    }

    if (recurrenceValue?.id !== 'Do Not Repeat' && !startDate) {
      return 'Please select a Start Date for the schedule';
    }

    if (
      recurrenceValue?.id === 'MONTHLY' &&
      monthlyOption === 'date' &&
      monthDates.length === 0
    ) {
      return 'Please select Month Date(s)';
    }
    // if (!dueDate) {
    //   return 'Please select "Due date"';
    // }

    const selectedDate = new Date(dueDate as string);
    const today = new Date();
    const todayAtMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const selectedEndDate = endDate ? new Date(endDate) : null;
    if (
      recurrenceValue?.id !== 'Do Not Repeat' &&
      endDate &&
      selectedEndDate &&
      selectedEndDate < today
    ) {
      return 'End date cannot be in the past';
    }
    if (selectedDate < todayAtMidnight) {
      return "Due date cannot be before today's date";
    }
    if (
      recurrenceValue?.id === 'WEEKLY' &&
      (!repeatOnDays || repeatOnDays.length === 0)
    ) {
      return 'Please select at least one day for Weekly recurrence';
    }

    if (recurrenceValue?.id !== 'Do Not Repeat' && !dueDayValue) {
      return `Please select 'Due in' to  set the due date for the run`;
    }
  }

  async function startAssign() {
    // Reset
    setErrorMsg('');
    setSuccessMsg('');
    setIsLoading(true);

    // Validation
    const errorMessage = validate();

    if (errorMessage) {
      setErrorMsg(errorMessage);
      setIsLoading(false);

      return;
    }

    const runId = uuidv4();

    const runsAssignment = assignTo?.map(user => {
      return {
        run_id: runId,
        user_id: user?.id
      };
    });

    // Create a Run
    const { data: _runDetails, extensions: _projectExtentions } =
      await createRunAndAssignMutation({
        variables: {
          runs: [
            {
              id: runId,
              name: runName,
              due_at: dueDate,
              checklist_id: props?.checklistId,
              checklist_version_id: props?.checklistVersionId
            }
          ],
          runsAssignment: runsAssignment
        }
      });

    if (_runDetails?.['insert_runs']) {
      // 2) Fire native notification

      if (globalBrowserEnabled) {
        // Fire native browser notification
        // fireNativeNotification(
        //   'Checklist Assigned',
        //   `Run ${runName} has been assigned to ${assignTo
        //     .map(u => u.displayName)
        //     .join(', ')}.`
        // );
      }

      // 🔄 Loop each assignee, check preference, then insert notification
      await Promise.all(
        assignTo.map(async user => {
          const allowNotify = await getNotificationPreference(user.id);
          // console.log(`User ${user.displayName} preference:`, allowNotify);

          if (
            allowNotify &&
            globalBrowserEnabled &&
            !selectedOrganization?.pricing_subscription?.pricing_plan
              ?.pricing_plan_type?.is_freeplan
          ) {
            await insertNotification({
              variables: {
                user_id: user.id,
                org_id: selectedOrganization?.id,
                project_id: props?.projectId,
                event_type: 'assigned_to_you',
                title: 'Checklist Assigned',
                body: `Run "${runName}" has been assigned to you.`,
                notify_at: new Date().toISOString(),
                is_sent: user.id === userId,
                created_by: userId,
                run_id: runId // link to the run
              }
            });
          }
        })
      );

      // 2️⃣  Schedule “due_date_reached” notifications
      await Promise.all(
        assignTo.map(async user => {
          // fetch each user’s “due_date_reached” pref
          const allowDueNotify = await client
            .query<{
              user_notification_preferences: { browser_enabled: boolean }[];
            }>({
              query: GET_ASSIGNED_PREF,
              variables: {
                user_id: user.id,
                org_id: selectedOrganization?.id,
                event_type: 'due_date_reached'
              }
            })
            .then(r => r.data.user_notification_preferences[0]?.browser_enabled)
            .catch(() => false);

          if (
            allowDueNotify &&
            dueDate &&
            globalBrowserEnabled &&
            !selectedOrganization?.pricing_subscription?.pricing_plan
              ?.pricing_plan_type?.is_freeplan
          ) {
            // bump it to 23:59:00 local time
            const dueDateObject = new Date(dueDate || '');
            dueDateObject.setHours(23, 59, 0, 0);

            // toISOString() will give the correct UTC‐normalized string,
            // e.g. "2025-07-30T23:59:00.000Z" (if you're in UTC),
            // or "2025-07-30T23:59:00.000-04:00" (if you're UTC‑4), etc.
            const notifyAt = dueDateObject.toISOString();

            await insertNotification({
              variables: {
                user_id: user.id,
                org_id: selectedOrganization?.id,
                project_id: props.projectId,
                event_type: 'due_date_reached',
                title: 'Checklist Due',
                body: `The Checklist "${runName}" is due.`,
                notify_at: notifyAt!, // your ISO timestamp
                is_sent: false, // scheduled, not yet sent
                created_by: userId,
                run_id: runId // link to the run
              }
            });
          }
        })
      );
      // Capture the event
      posthog.capture?.('Checklist Assigned', {
        eventDate: new Date().toISOString().split('T')[0]
      });
      const currentUsage =
        useOrganizationStore.getState().organizationGetStartedProgress;
      useOrganizationStore.getState().setOrganizationGetStartedProgress({
        ...currentUsage,
        assignedToOthersRunsCount:
          (typeof currentUsage.runs === 'number' ? currentUsage.runs : 0) + 1
      });

      // if (
      //   selectedOrganization?.pricing_subscription?.pricing_plan
      //     ?.pricing_plan_type?.is_freeplan &&
      //   selectedOrganization?.pricing_subscription?.pricing_plan
      //     ?.pricing_limitations &&
      //   selectedOrganization?.pricing_subscription?.pricing_plan
      //     ?.pricing_limitations.length > 0
      // ) {
      //   const currentUsage = useOrganizationStore.getState().organizationUsage;
      //   useOrganizationStore.getState().setOrganizationUsage({
      //     ...currentUsage,
      //     runs: (currentUsage.runs || 0) + 1
      //   });
      // }
    }

    if (_runDetails?.['insert_runs']) {
      // Done
      setRunName('');
      setDueDate(tomorrowDate.toLocaleDateString('en-CA'));
      setAssignTo([]);
      setIsLoading(false);
      setSuccessMsg('Checklist is successfully assigned.');
    }
  }

  async function checkRunLimitation() {
    if (
      selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_limitations &&
      selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_limitations.length > 0
    ) {
      const runChecklistLimitation =
        selectedOrganization?.pricing_subscription?.pricing_plan?.pricing_limitations.find(
          limitation => limitation.type === 'runs'
        );

      if (
        runChecklistLimitation &&
        runChecklistLimitation?.value &&
        organizationUsage.runs &&
        organizationUsage.runs >= parseInt(runChecklistLimitation?.value)
      ) {
        return setShowUpgrade(true);
      } else {
        return setShowUpgrade(false);
      }
    }
  }

  /**
   * Determines when next schedule run will happen
   */
  function computeNextRunAt(
    schedule: {
      recurrence_type: string;
      repeat_every: number;
      start_at: string; // new: ISO timestamp from user
      end_date: string;
      repeat_on_days?: number[] | null;
      repeat_on_month_dates?: number[] | null;
      repeat_on_week_number?: number | null;
      repeat_on_week_day?: number | null;
      last_run_at?: string | null;
      time_zone: string; // IANA zone, if you need for future DST logic
      monthly_option?: 'date' | 'weekday';
    },
    currentDate: Date
  ): Date | null {
    const {
      recurrence_type,
      repeat_every,
      start_at,
      end_date,
      repeat_on_days,
      repeat_on_month_dates,
      repeat_on_week_number,
      repeat_on_week_day,
      last_run_at,
      time_zone,
      monthly_option
    } = schedule;

    // console.log('[computeNextRunAt] schedule:', schedule);
    // console.log('[computeNextRunAt] now:', currentDate.toISOString());

    const endDateObj = new Date(end_date);
    if (currentDate > endDateObj) {
      // console.log('→ expired');
      return null;
    }

    // Base timestamp: last_run_at (if we’ve run before) or the original start_at
    // let nextDate = last_run_at ? new Date(last_run_at) : new Date(start_at);
    // console.log('→ base for next:', new Date(start_at).toISOString());

    if (recurrence_type === 'DAILY') {
      const startAtDate = new Date(start_at);
      const targetHour = startAtDate.getHours();
      const targetMin = startAtDate.getMinutes();

      const candidate = last_run_at
        ? new Date(last_run_at)
        : new Date(currentDate);
      candidate.setHours(targetHour, targetMin, 0, 0);

      // If the computed time is still in the past or now, shift to future
      while (candidate <= currentDate) {
        candidate.setDate(candidate.getDate() + repeat_every);
      }

      // console.log('→ next daily:', candidate.toISOString());
      return candidate <= endDateObj ? candidate : null;
    }

    if (recurrence_type === 'WEEKLY' && repeat_on_days?.length) {
      const startAtDate = new Date(start_at);
      const baseDate = last_run_at ? new Date(last_run_at) : startAtDate;

      const targetHour = startAtDate.getHours();
      const targetMin = startAtDate.getMinutes();

      const candidate = new Date(baseDate);

      // Start from the day after the base if we're already at/after it
      if (candidate <= currentDate) {
        candidate.setDate(candidate.getDate() + 1);
      }

      while (candidate <= endDateObj) {
        const weekday = candidate.getDay();

        const weeksSinceStart = Math.floor(
          (candidate.getTime() - startAtDate.getTime()) /
            (1000 * 60 * 60 * 24 * 7)
        );

        const isValidWeek = weeksSinceStart % repeat_every === 0;
        const isValidDay = repeat_on_days.includes(weekday);

        if (isValidWeek && isValidDay) {
          candidate.setHours(targetHour, targetMin, 0, 0);
          if (candidate > currentDate) {
            // console.log('→ next weekly:', candidate.toISOString());
            return candidate;
          }
        }

        candidate.setDate(candidate.getDate() + 1);
      }

      // console.log('→ no weekly candidate');
    }

    // MONTHLY
    if (recurrence_type === 'MONTHLY') {
      const usingDates = schedule.monthly_option === 'date';
      const usingWeekday = schedule.monthly_option === 'weekday';

      const now = new Date();
      const endDt = new Date(end_date);
      if (now > endDt) return null;

      const origin = new Date(start_at);
      const H = origin.getHours();
      const M = origin.getMinutes();
      const S = origin.getSeconds();
      const days = Array.isArray(repeat_on_month_dates)
        ? [...repeat_on_month_dates].sort((a, b) => a - b)
        : [];
      const nthWeek = repeat_on_week_number;
      const nthWeekday = repeat_on_week_day;

      // helper to scan a given year/month
      const scanMonth = (year: number, month: number): Date | null => {
        // 1) specific dates
        if (usingDates) {
          for (const d of days) {
            const cand = new Date(year, month, d, H, M, S);
            if (cand > now && cand <= endDt) return cand;
          }
        }
        // 2) Nth‑weekday
        if (
          usingWeekday &&
          typeof nthWeek === 'number' &&
          typeof nthWeekday === 'number'
        ) {
          const firstOf = new Date(year, month, 1);
          const offset = (nthWeekday - firstOf.getDay() + 7) % 7;
          const d = 1 + offset + (nthWeek - 1) * 7;
          const cand = new Date(year, month, d, H, M, S);
          if (cand > now && cand <= endDt) return cand;
        }
        return null;
      };

      // 1) try THIS month
      const y = now.getFullYear();
      const m = now.getMonth();
      let found = scanMonth(y, m);
      if (found) return found;

      // 2) bump month-by-‘repeat_every’ until we find something
      for (let step = repeat_every; step <= 120; step += repeat_every) {
        const nextMonth = new Date(y, m + step, 1, H, M, S);
        if (nextMonth > endDt) break;
        const fmYear = nextMonth.getFullYear();
        const fmMonth = nextMonth.getMonth();
        found = scanMonth(fmYear, fmMonth);
        if (found) return found;
      }

      return null;
    } else if (recurrence_type === 'YEARLY') {
      const startAtDate = new Date(start_at);
      const targetMonth = startAtDate.getMonth();
      const targetDay = startAtDate.getDate();
      const targetHour = startAtDate.getHours();
      const targetMin = startAtDate.getMinutes();
      const targetSec = startAtDate.getSeconds();

      // seed candidate either at last_run_at or the very first start_at
      let candidate = last_run_at ? new Date(last_run_at) : new Date(start_at);

      // normalize to this year’s occurrence
      candidate = new Date(
        candidate.getFullYear(),
        targetMonth,
        targetDay,
        targetHour,
        targetMin,
        targetSec
      );

      // if that’s already past (or now), bump by repeat_every years
      while (candidate <= currentDate) {
        candidate.setFullYear(candidate.getFullYear() + repeat_every);
      }

      // check against end_date
      return candidate <= endDateObj ? candidate : null;
    }

    // console.log('→ no next run');
    return null;
  }

  function handleSchedule() {
    setErrorMsg('');
    setSuccessMsg('');

    const dateTimeString = `${startDate} ${startTime}`; // e.g. "2025-04-16 12:00 AM"
    const FORMAT = 'YYYY-MM-DD hh:mm A';

    const m = moment.tz(dateTimeString, FORMAT, timeZoneValue!.id);
    if (!m.isValid()) {
      console.error(
        'Invalid date/time:',
        dateTimeString,
        'with format',
        FORMAT
      );
      setErrorMsg(
        'Could not parse the selected date & time. Please check your input.'
      );
      return;
    }
    const startAtISO = m.toISOString();
    // console.log('startAtISO:', startAtISO);

    // Example: treat endDate as end-of-day in selected timezone
    const END_FORMAT = 'YYYY-MM-DD hh:mm A';
    const endDateTimeString = `${endDate} 12:00 AM`; // or a fixed cutoff time

    const endMoment = moment.tz(
      endDateTimeString,
      END_FORMAT,
      timeZoneValue!.id
    );
    const endAtISO = endMoment.isValid() ? endMoment.toISOString() : null;

    const errorMessage = validate();
    if (errorMessage) {
      setErrorMsg(errorMessage);
      return;
    }

    if (
      !props.checklistId ||
      !recurrenceValue ||
      !repeatValue ||
      !dueDayValue ||
      assignTo.length === 0
    ) {
      if (assignTo.length === 0) {
        setErrorMsg('Please select an Assignee');
        return;
      }
      setErrorMsg('All fields are required');
      return;
    }

    // const today = new Date();
    const schedule = {
      recurrence_type: recurrenceValue?.id,
      repeat_every: parseInt(repeatValue?.id),
      end_date: endAtISO as string,
      repeat_on_days:
        recurrenceValue!.id === 'WEEKLY' ? repeatOnDays : undefined,
      time_zone: timeZoneValue?.id as string,
      start_at: startAtISO,
      repeat_on_month_dates: monthlyOption === 'date' ? monthDates : null,
      repeat_on_week_number:
        monthlyOption === 'weekday' ? monthWeekNumber : null,
      repeat_on_week_day: monthlyOption === 'weekday' ? monthWeekDay : null,
      monthly_option: monthlyOption
    };

    // ⛔ Check if computed next run is null — likely because end_date is before the next valid run
    const nextRunPreview = computeNextRunAt(schedule, new Date());
    if (!nextRunPreview) {
      setErrorMsg(
        'The end date is before the next possible run. Please extend the end date or adjust the recurrence settings.'
      );
      return;
    }

    const scheduleAssignId = uuidv4();
    createScheduleConfiguration({
      variables: {
        checklistId: props.checklistId,
        recurrenceType: recurrenceValue.id,
        repeatEvery: parseInt(repeatValue.id, 10),
        repeatOnDays: recurrenceValue.id === 'WEEKLY' ? repeatOnDays : null,
        dueInDays: parseInt(dueDayValue.id, 10),
        endDate: endAtISO,
        scheduleAssignId: scheduleAssignId,
        createdBy: userId,
        checklistVersionId: props.checklistVersionId,
        name: runName,
        timeZone: timeZoneValue?.id,
        startAt: startAtISO,
        repeatOnMonthDates: monthlyOption === 'date' ? monthDates : null,
        repeatOnWeekNumber:
          monthlyOption === 'weekday' ? monthWeekNumber : null,
        repeatOnWeekDay: monthlyOption === 'weekday' ? monthWeekDay : null
      }
    })
      .then(response => {
        const scheduleId = response.data.insert_schedule_configuration_one.id; // ✅ Get inserted ID
        const createdAt =
          response.data.insert_schedule_configuration_one.created_at;

        const assignments = assignTo.map(user => ({
          schedule_id: scheduleId, // ✅ Correctly reference schedule_id
          user_id: user.id,
          created_by: userId
        }));
        // console.log(assignments);

        return createScheduleAssignments({
          variables: { assignments }
        }).then(() => ({
          createdAt,
          scheduleId
        }));
      })
      .then(({ createdAt, scheduleId }) => {
        const today = new Date();
        const schedule = {
          recurrence_type: recurrenceValue?.id,
          repeat_every: parseInt(repeatValue?.id),
          created_at: createdAt,
          end_date: endAtISO as string,
          repeat_on_days:
            recurrenceValue!.id === 'WEEKLY' ? repeatOnDays : undefined,
          time_zone: timeZoneValue?.id as string,
          start_at: startAtISO,
          repeat_on_month_dates: monthlyOption === 'date' ? monthDates : null,
          repeat_on_week_number:
            monthlyOption === 'weekday' ? monthWeekNumber : null,
          repeat_on_week_day: monthlyOption === 'weekday' ? monthWeekDay : null,
          monthly_option: monthlyOption
        };
        // console.log('[handleSchedule] Generated Schedule Object:', schedule);

        const nextRunAt = computeNextRunAt(schedule, today);

        // console.log(
        //   '[handleSchedule] Computed nextRunAt:',
        //   nextRunAt?.toISOString() ?? 'null'
        // );

        return updateNextRun({
          variables: {
            scheduleId,
            nextRunAt: nextRunAt?.toISOString() ?? null
          }
        });
      })
      .then(() => {
        setSuccessMsg(<>Run successfully scheduled!</>);
        setRunName('');
        setEndDate(null);
        setRecurrenceValue(undefined);
        setRepeatValue(undefined);
        setAssignTo([]);
        setDueDayValue(undefined);
        setIsOpen(false);
      })
      .catch(error => {
        setErrorMsg('Failed to schedule run: ' + error.message);
      });
  }

  const repetitionSummary = useMemo(() => {
    if (!recurrenceValue || !repeatValue || !dueDayValue || !timeZoneValue) {
      return '';
    }

    const repeatEvery = parseInt(repeatValue.id, 10);
    const dueInDays = parseInt(dueDayValue.id, 10);

    // ordinal helper
    const ordinal = (n: number) => {
      const rem100 = n % 100;
      if (rem100 >= 11 && rem100 <= 13) return 'th';
      const rem10 = n % 10;
      if (rem10 === 1) return 'st';
      if (rem10 === 2) return 'nd';
      if (rem10 === 3) return 'rd';
      return 'th';
    };

    let recurrenceText = 'Repeats';

    const dateTimeString = `${startDate} ${startTime}`; // e.g. "2025-04-16 12:00 AM"
    const FORMAT = 'YYYY-MM-DD hh:mm A';

    const m = moment.tz(dateTimeString, FORMAT, timeZoneValue!.id);
    if (!m.isValid()) {
      console.error(
        'Invalid date/time:',
        dateTimeString,
        'with format',
        FORMAT
      );
      setErrorMsg(
        'Could not parse the selected date & time. Please check your input.'
      );
      return;
    }
    const startAtISO = m.toISOString();

    // Example: treat endDate as end-of-day in selected timezone
    const END_FORMAT = 'YYYY-MM-DD hh:mm A';
    const endDateTimeString = `${endDate} 12:00 AM`; // or a fixed cutoff time

    const endMoment = moment.tz(
      endDateTimeString,
      END_FORMAT,
      timeZoneValue!.id
    );
    const endAtISO = endMoment.isValid() ? endMoment.toISOString() : null;

    // prepare the schedule object for next‑run calculation:
    const scheduleObj = {
      recurrence_type: recurrenceValue.id,
      repeat_every: repeatEvery,
      start_at: startAtISO, // you’ll need to have this in scope
      end_date: endAtISO as string, // ditto
      repeat_on_days: repeatOnDays,
      repeat_on_month_dates: monthDates,
      repeat_on_week_number: monthWeekNumber,
      repeat_on_week_day: monthWeekDay,
      last_run_at: null, // or null
      time_zone: timeZoneValue.id,
      monthly_option: monthlyOption
    };

    const nextRun = computeNextRunAt(scheduleObj, new Date());

    // build the verbal recurrence…
    switch (recurrenceValue.id) {
      case 'DAILY':
        if (repeatEvery === 1) recurrenceText += ' daily';
        else if (repeatEvery === 2) recurrenceText += ' every other day';
        else recurrenceText += ` every ${repeatEvery} days`;
        break;

      case 'WEEKLY': {
        const dayLabels = repeatOnDays
          .map(d => weekDayOptions.find(w => w.id === d)?.displayName)
          .filter(Boolean) as string[];
        const days =
          dayLabels.length === 1
            ? dayLabels[0]
            : dayLabels.length === 2
            ? `${dayLabels[0]} & ${dayLabels[1]}`
            : `${dayLabels.slice(0, -1).join(', ')} & ${dayLabels.slice(-1)}`;
        if (repeatEvery === 1) recurrenceText += ` every week on ${days}`;
        else if (repeatEvery === 2)
          recurrenceText += ` every other week on ${days}`;
        else recurrenceText += ` every ${repeatEvery} weeks on ${days}`;
        break;
      }

      case 'MONTHLY':
        if (repeatEvery === 1) recurrenceText += ' monthly';
        else if (repeatEvery === 2) recurrenceText += ' every other month';
        else recurrenceText += ` every ${repeatEvery} months`;

        if (monthlyOption === 'date' && monthDates.length) {
          const sorted = [...monthDates].sort((a, b) => a - b);
          if (sorted.length === 1) {
            const d = sorted[0];
            recurrenceText += ` on the ${d}${ordinal(d)}`;
          } else {
            const labels = sorted.map(n => `${n}${ordinal(n)}`);
            const last = labels.pop();
            recurrenceText += ` on the ${labels.join(', ')} & ${last}`;
          }
        }

        if (
          monthlyOption === 'weekday' &&
          monthWeekNumber != null &&
          monthWeekDay != null
        ) {
          const ordWords = ['zeroth', 'first', 'second', 'third', 'fourth'];
          const wkdays = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday'
          ];
          recurrenceText += ` on the ${ordWords[monthWeekNumber]} ${wkdays[monthWeekDay]}`;
        }
        break;

      case 'YEARLY':
        if (repeatEvery === 1) recurrenceText += ' yearly';
        else if (repeatEvery === 2) recurrenceText += ' every other year';
        else recurrenceText += ` every ${repeatEvery} years`;
        break;
    }

    let dueText = '';
    if (dueInDays === 0) dueText = ' due same day';
    else if (dueInDays === 1) dueText = ' due next day';
    else dueText = ` due ${dueInDays} days after`;

    const nextText = nextRun
      ? ` Next run: ${moment(nextRun)
          .tz(timeZoneValue.id)
          .format('MMM D, YYYY hh:mm A')}`
      : ' No further runs (past end date)';

    return `${recurrenceText},${dueText}.${nextText}`;
  }, [
    recurrenceValue,
    repeatValue,
    dueDayValue,
    repeatOnDays,
    monthlyOption,
    monthDates,
    monthWeekNumber,
    monthWeekDay,
    endDate,
    startDate,
    startTime,
    timeZoneValue
  ]);

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setIsOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-6 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`w-full ${
                    showUpgrade && successMsg ? 'max-w-fit' : 'max-w-md'
                  } transform rounded-lg bg-white p-4 text-left align-middle shadow-xl transition-all`}
                >
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        Assign checklist
                      </Dialog.Title>
                    </div>

                    <div>
                      <CLButton
                        className="p-2"
                        tabIndex={-1}
                        onClick={() => {
                          setIsOpen(false);
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L8 6.58579L14.2929 0.292893C14.6834 -0.0976311 15.3166 -0.0976311 15.7071 0.292893C16.0976 0.683417 16.0976 1.31658 15.7071 1.70711L9.41421 8L15.7071 14.2929C16.0976 14.6834 16.0976 15.3166 15.7071 15.7071C15.3166 16.0976 14.6834 16.0976 14.2929 15.7071L8 9.41421L1.70711 15.7071C1.31658 16.0976 0.683417 16.0976 0.292893 15.7071C-0.0976311 15.3166 -0.0976311 14.6834 0.292893 14.2929L6.58579 8L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      </CLButton>
                    </div>
                  </div>

                  {/* Select assignee */}
                  <div className="mt-4">
                    <CLPrimarySelect
                      label="Assign to *"
                      isMulti={true}
                      value={assignTo}
                      isSearchable={true}
                      options={projectUsers}
                      getOptionLabel={option => option.displayName}
                      getOptionValue={option => option.id}
                      onChange={selectedUsers => {
                        if (Array.isArray(selectedUsers)) {
                          setAssignTo([...selectedUsers]);
                        }
                      }}
                      noOptionsMessage={() => 'No users found'}
                    />
                  </div>

                  {/* Invite a member */}
                  {props.isFromDesignPage &&
                    props.projectId &&
                    (newProjectIds[props.projectId] ||
                      projectPermissions?.[props.projectId]?.[
                        PERMISSIONS_CONSTANTS.MANAGE_PROJECT_MEMBERS
                      ]) && (
                      <div className="text-gray-600 text-sm flex items-center justify-end pt-2">
                        <span
                          className="underline underline-offset-2 cursor-pointer"
                          onClick={() => setIsInviteOpen(true)}
                        >
                          + Invite New Members
                        </span>
                      </div>
                    )}

                  {/* Run name */}
                  <div className="mt-4">
                    <CLPrimaryInput
                      type="text"
                      label="Run name *"
                      value={runName}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setRunName(event.target.value);
                      }}
                    />
                  </div>

                  <div className="mt-4 ">
                    {/* Recurrence */}
                    {/* <div className="flex flex-col w-1/2"> */}
                    <CLPrimarySelect
                      label="Recurrence *"
                      isMulti={false}
                      value={recurrenceValue}
                      options={recurrenceOptions}
                      getOptionLabel={option => option.displayName}
                      getOptionValue={option => option.id}
                      onChange={selectedOption => {
                        setRecurrenceValue(
                          selectedOption as
                            | { id: string; displayName: string }
                            | undefined
                        );
                      }}
                    />
                  </div>

                  {recurrenceValue?.id !== 'Do Not Repeat' && (
                    <>
                      <div className="mt-4 flex space-x-2">
                        <div className="flex-1">
                          <CLPrimaryDate
                            type="date"
                            label="Start Date *"
                            value={startDate}
                            onChange={e =>
                              setStartDate((e.target as HTMLInputElement).value)
                            }
                          />
                        </div>
                        <div className="w-1/2 flex flex-col justify-center">
                          <div>
                            {/* 12h‑only time picker */}
                            <label className="block text-black/40">
                              Start Time
                            </label>
                            <Datetime
                              dateFormat={false} // hide calendar
                              timeFormat="hh:mm A" // 12h + AM/PM
                              value={startTime}
                              onChange={val => {
                                const v =
                                  typeof val === 'string'
                                    ? val
                                    : val.format('hh:mm A');
                                setStartTime(v);
                              }}
                              inputProps={{
                                placeholder: 'Select time',
                                className: styles.cl_input_field
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 ">
                        <CLPrimarySelect
                          label="Time Zone *"
                          isMulti={false}
                          value={
                            timeZoneValue as
                              | {
                                  id: string;
                                  fullLabel: string;
                                  label: string;
                                  value: string;
                                }
                              | undefined
                          }
                          options={tzList}
                          getOptionValue={opt => opt.id}
                          formatOptionLabel={(opt, { context }) => {
                            // context === 'menu' for the dropdown list
                            // context === 'value' for the single-value (selected) display
                            return context === 'menu'
                              ? opt.fullLabel
                              : opt.label;
                          }}
                          onChange={opt =>
                            setTimeZoneValue(opt as (typeof tzList)[0])
                          }
                        />
                      </div>

                      {recurrenceValue?.id === 'WEEKLY' && (
                        <div className="mt-4">
                          <label className="text-sm font-medium text-black/40 mb-2 block">
                            Repeat on *
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {weekDayOptions.map(day => (
                              <CLButton
                                key={day.id}
                                type="button"
                                className={`px-3 py-1 border rounded-lg text-sm transition ${
                                  repeatOnDays.includes(day.id)
                                    ? 'bg-[rgb(var(--primary-color-rgb))]'
                                    : 'bg-white text-black/80 border-gray-300'
                                }`}
                                onClick={() => {
                                  setRepeatOnDays(prev =>
                                    prev.includes(day.id)
                                      ? prev.filter(d => d !== day.id)
                                      : [...prev, day.id]
                                  );
                                }}
                              >
                                {day.label}
                              </CLButton>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Repeat Every */}
                      <div className="mt-4">
                        {/* <div className="flex flex-col w-1/2"> */}
                        {/* Label with Tooltip */}
                        <div className="flex items-center gap-[6px] mb-1 text-black/40 text-sm">
                          <span>Repeat every *</span>
                          <CLTooltip content="Set the interval for repetition">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              opacity={0.6}
                              className="mt-0.5"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M7.00015 0C3.13443 0 0.0015564 3.13336 0 6.99939C0.0015564 10.8656 3.13443 13.9981 7.00015 14C10.8662 13.9981 13.9988 10.8656 14 6.99939C13.9988 3.13336 10.8662 0 7.00015 0ZM7.00015 12.8257C3.78311 12.8206 1.18005 10.2167 1.17383 6.99939C1.1799 3.78204 3.78311 1.17834 7.00015 1.17261C10.2169 1.17834 12.8207 3.78204 12.8256 6.99939C12.8207 10.2167 10.2169 12.8206 7.00015 12.8257ZM6.99927 4.87048C7.50403 4.87048 7.91812 4.45764 7.91812 3.9519C7.91812 3.44684 7.50403 3.03381 6.99927 3.03381C6.49387 3.03381 6.08008 3.44684 6.08008 3.9519C6.08008 4.45776 6.49387 4.87048 6.99927 4.87048ZM6.17279 10.9659H7.8374L7.83755 5.67438H6.17279V10.9659Z"
                                fill="#808080"
                              />
                            </svg>
                          </CLTooltip>
                        </div>
                        <CLPrimarySelect
                          isMulti={false}
                          value={repeatValue}
                          isSearchable={false}
                          options={Array.from({ length: 10 }, (_, i) => ({
                            id: (i + 1).toString(),
                            displayName: (i + 1).toString()
                          }))}
                          getOptionLabel={option => {
                            const unit =
                              recurrenceValue?.id === 'WEEKLY'
                                ? option.displayName === '1'
                                  ? 'week'
                                  : 'weeks'
                                : recurrenceValue?.id === 'MONTHLY'
                                ? option.displayName === '1'
                                  ? 'month'
                                  : 'months'
                                : recurrenceValue?.id === 'YEARLY'
                                ? option.displayName === '1'
                                  ? 'year'
                                  : 'years'
                                : option.displayName === '1'
                                ? 'day'
                                : 'days';
                            return `${option.displayName} ${unit}`;
                          }}
                          getOptionValue={option => option.id}
                          onChange={selectedOption => {
                            setRepeatValue(
                              selectedOption as
                                | { id: string; displayName: string }
                                | undefined
                            );
                          }}
                        />
                        {/* </div> */}
                      </div>

                      {recurrenceValue?.id === 'MONTHLY' && (
                        <div className="mt-4 space-y-2">
                          <label className="font-medium text-black/40 text-sm">
                            Repeat On *
                          </label>
                          <div className="flex items-center gap-4">
                            <label className="cursor-pointer inline-flex items-center text-black/80 text-sm">
                              <input
                                type="radio"
                                className={`mr-2 ${
                                  monthlyOption === 'date'
                                    ? '!bg-[rgb(var(--primary-color-rgb))] !color-[rgb(var(--primary-color-rgb))]'
                                    : '!bg-none'
                                } `}
                                checked={monthlyOption === 'date'}
                                onChange={() => setMonthlyOption('date')}
                              />
                              Date(s)
                            </label>
                            <label className="cursor-pointer inline-flex items-center text-black/80 text-sm">
                              <input
                                type="radio"
                                className={`mr-2 borderS ${
                                  monthlyOption === 'weekday'
                                    ? '!bg-[rgb(var(--primary-color-rgb))] ring-[rgb(var(--primary-color-rgb))]! !color-[rgb(var(--primary-color-rgb))] !border-[rgb(var(--primary-color-rgb))] !accent-[rgb(var(--primary-color-rgb))]'
                                    : '!bg-none !ring-transparent'
                                }`}
                                checked={monthlyOption === 'weekday'}
                                onChange={() => setMonthlyOption('weekday')}
                              />
                              Weekday
                            </label>
                          </div>

                          {monthlyOption === 'date' && (
                            <CLPrimarySelect
                              label="Select date(s) of the month *"
                              isMulti
                              options={Array.from({ length: 31 }, (_, i) => ({
                                id: (i + 1).toString(),
                                displayName: (i + 1).toString()
                              }))}
                              getOptionValue={o => o.id}
                              getOptionLabel={o => {
                                const n = parseInt(o.displayName, 10);
                                // special case for 11th, 12th, 13th
                                const rem100 = n % 100;
                                let suffix = 'th';
                                if (rem100 < 11 || rem100 > 13) {
                                  const rem10 = n % 10;
                                  if (rem10 === 1) suffix = 'st';
                                  else if (rem10 === 2) suffix = 'nd';
                                  else if (rem10 === 3) suffix = 'rd';
                                }
                                return `${n}${suffix}`;
                              }}
                              value={monthDates.map(d => ({
                                id: d.toString(),
                                displayName: d.toString()
                              }))}
                              onChange={opts =>
                                setMonthDates(
                                  (opts as unknown as { id: string }[]).map(o =>
                                    parseInt(o.id, 10)
                                  )
                                )
                              }
                              menuPlacement="top"
                            />
                          )}

                          {monthlyOption === 'weekday' && (
                            <div className="mt-8 flex gap-4">
                              <div className="flex flex-col w-1/2 mb-1  text-sm">
                                <span className="mb-2 text-black/40">
                                  Week of month *
                                </span>
                                <CLPrimarySelect
                                  isMulti={false}
                                  options={[
                                    { id: '1', displayName: 'First' },
                                    { id: '2', displayName: 'Second' },
                                    { id: '3', displayName: 'Third' },
                                    { id: '4', displayName: 'Fourth' }
                                  ]}
                                  getOptionValue={o => o.id}
                                  getOptionLabel={o => o.displayName}
                                  value={
                                    monthWeekNumber
                                      ? {
                                          id: monthWeekNumber.toString(),
                                          displayName: [
                                            '',
                                            'First',
                                            'Second',
                                            'Third',
                                            'Fourth'
                                          ][monthWeekNumber]
                                        }
                                      : undefined
                                  }
                                  onChange={opt =>
                                    setMonthWeekNumber(
                                      parseInt((opt as { id: string }).id, 10)
                                    )
                                  }
                                  menuPlacement="top"
                                />
                              </div>

                              <div className="flex flex-col w-1/2  mb-1 text-sm">
                                <span className="mb-2 text-black/40">
                                  Day of week *
                                </span>
                                <CLPrimarySelect
                                  isMulti={false}
                                  options={[
                                    { id: '0', displayName: 'Sunday' },
                                    { id: '1', displayName: 'Monday' },
                                    { id: '2', displayName: 'Tuesday' },
                                    { id: '3', displayName: 'Wednesday' },
                                    { id: '4', displayName: 'Thursday' },
                                    { id: '5', displayName: 'Friday' },
                                    { id: '6', displayName: 'Saturday' }
                                  ]}
                                  getOptionValue={o => o.id}
                                  getOptionLabel={o => o.displayName}
                                  value={
                                    monthWeekDay !== null
                                      ? {
                                          id: monthWeekDay.toString(),
                                          displayName: [
                                            'Sunday',
                                            'Monday',
                                            'Tuesday',
                                            'Wednesday',
                                            'Thursday',
                                            'Friday',
                                            'Saturday'
                                          ][monthWeekDay]
                                        }
                                      : undefined
                                  }
                                  onChange={opt =>
                                    setMonthWeekDay(
                                      parseInt((opt as { id: string }).id, 10)
                                    )
                                  }
                                  menuPlacement="top"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-4 flex gap-4">
                        {/* Due in Day(s) */}
                        <div className="flex flex-col w-1/2">
                          {/* Label with Tooltip */}
                          <div className="flex items-center gap-[6px] mb-1 text-black/40 text-sm">
                            <span>Due in *</span>
                            <CLTooltip content="Due date set from the date of assignment">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                opacity={0.6}
                                className="mt-1"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M7.00015 0C3.13443 0 0.0015564 3.13336 0 6.99939C0.0015564 10.8656 3.13443 13.9981 7.00015 14C10.8662 13.9981 13.9988 10.8656 14 6.99939C13.9988 3.13336 10.8662 0 7.00015 0ZM7.00015 12.8257C3.78311 12.8206 1.18005 10.2167 1.17383 6.99939C1.1799 3.78204 3.78311 1.17834 7.00015 1.17261C10.2169 1.17834 12.8207 3.78204 12.8256 6.99939C12.8207 10.2167 10.2169 12.8206 7.00015 12.8257ZM6.99927 4.87048C7.50403 4.87048 7.91812 4.45764 7.91812 3.9519C7.91812 3.44684 7.50403 3.03381 6.99927 3.03381C6.49387 3.03381 6.08008 3.44684 6.08008 3.9519C6.08008 4.45776 6.49387 4.87048 6.99927 4.87048ZM6.17279 10.9659H7.8374L7.83755 5.67438H6.17279V10.9659Z"
                                  fill="#808080"
                                />
                              </svg>
                            </CLTooltip>
                          </div>
                          <CLPrimarySelect
                            isMulti={false}
                            value={dueDayValue}
                            isSearchable={false}
                            options={Array.from({ length: 11 }, (_, i) => ({
                              id: i.toString(),
                              displayName: i.toString()
                            }))}
                            getOptionLabel={option =>
                              `${option.displayName} ${
                                option.displayName === '1' ? 'day' : 'days'
                              }`
                            }
                            getOptionValue={option => option.id}
                            onChange={selectedOption => {
                              setDueDayValue(
                                selectedOption as
                                  | { id: string; displayName: string }
                                  | undefined
                              );
                            }}
                            menuPlacement="top"
                          />
                        </div>

                        {/* End Date */}
                        <div className="flex flex-col w-1/2">
                          <CLPrimaryDate
                            type="date"
                            label="End Date *"
                            value={endDate ? endDate.substring(0, 10) : ''}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setEndDate(e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {recurrenceValue?.id === 'Do Not Repeat' && (
                    <>
                      {/* Due date */}
                      <div className="mt-4">
                        <CLPrimaryDate
                          type="date"
                          label="Due date"
                          value={dueDate ? dueDate.substring(0, 10) : ''}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            if (event.target.value) {
                              setDueDate(
                                new Date(event.target.value).toISOString()
                              );
                            } else {
                              setDueDate('');
                            }
                          }}
                        />
                      </div>
                    </>
                  )}

                  {recurrenceValue?.id === 'Do Not Repeat' && (
                    <>
                      {/* Toolbar */}
                      <div className="mt-4 flex justify-between gap-2">
                        <div>
                          {/* Error message */}
                          {errorMsg && (
                            <div className="mt-2 text-red-400">{errorMsg}</div>
                          )}

                          {/* Success message */}
                          {successMsg && (
                            <div className="mt-2 whitespace-nowrap text-green-600">
                              {successMsg}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <CLSecondaryButton
                            onClick={() => {
                              setIsOpen(false);
                            }}
                          >
                            Cancel
                          </CLSecondaryButton>
                          {showUpgrade ? (
                            // <CLTooltip content="Upgrade To Assign">
                            <CLPrimaryButton
                              className="whitespace-nowrap"
                              onClick={() => {
                                setIsPlanAndBillingPopupOpen(true);
                              }}
                            >
                              Assign Run
                            </CLPrimaryButton>
                          ) : (
                            // </CLTooltip>
                            <CLPrimaryButton
                              disabled={isLoading}
                              onClick={() => {
                                startAssign();
                              }}
                            >
                              {isLoading && (
                                <LoadingSpinWhiteCustom className="w-4 h-4"></LoadingSpinWhiteCustom>
                              )}
                              Assign Run
                            </CLPrimaryButton>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {recurrenceValue?.id !== 'Do Not Repeat' && (
                    <>
                      {/* Draw line */}
                      <hr className="h-px mb-5 mt-6 bg-gray-200 border-0" />

                      <div className="mt-2 mb-5 text-sm text-gray-500">
                        {repetitionSummary && (
                          <>
                            <span className="text-gray-400">
                              Repetition Summary:
                            </span>{' '}
                            {repetitionSummary}
                          </>
                        )}
                      </div>

                      <div className="mt-4 flex justify-between gap-2">
                        <div>
                          {errorMsg && (
                            <div className="text-red-400">{errorMsg}</div>
                          )}
                          {successMsg && (
                            <div className="text-green-600">{successMsg}</div>
                          )}
                        </div>
                        <div className="flex gap-2 h-full">
                          <CLSecondaryButton onClick={() => setIsOpen(false)}>
                            Cancel
                          </CLSecondaryButton>
                          <CLPrimaryButton
                            onClick={() => {
                              if (
                                selectedOrganization &&
                                selectedOrganization?.pricing_subscription
                                  ?.pricing_plan?.pricing_plan_type
                                  ?.is_freeplan === true
                              ) {
                                setIsPlanAndBillingPopupOpen(true);
                              } else {
                                handleSchedule();
                              }
                            }}
                            disabled={isCreatingSchedule}
                          >
                            {isCreatingSchedule && (
                              <LoadingSpinWhiteCustom className="w-4 h-4"></LoadingSpinWhiteCustom>
                            )}
                            Schedule
                          </CLPrimaryButton>
                        </div>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <PlanAndBillingPopupComponent
        isOpen={isPlanAndBillingPopupOpen}
        setIsOpen={(value): void => {
          setIsPlanAndBillingPopupOpen(value);
          console.log('value:', value);
          if (!value) {
            setRecurrenceValue({
              id: 'Do Not Repeat',
              displayName: 'Do Not Repeat'
            });
          }
        }}
      ></PlanAndBillingPopupComponent>

      <ProjectMembersPopupComponent
        projectId={props.projectId}
        isOpen={isInviteOpen}
        setIsOpen={setIsInviteOpen}
        isDefaultOpen={false}
      />
    </>
  );
}
